/*
 * About Us — cloned from https://dacharya.com/about-us/
 * Uses the site's default teal/cream/orange palette + Georgia serif headings
 * so it flows with the Home & Contact pages.
 */
import React from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import {
  Award,
  AlertTriangle,
  Ear,
  Heart,
  Sparkles,
  Target,
  Compass,
  ArrowRight,
  Loader2,
  Phone,
  CheckCircle,
  Lightbulb,
  Headphones,
  MessageCircle,
  TrendingUp,
  Users,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Site palette (matched to Home & Contact pages)
const TEAL = '#0d6d64';
const TEAL_DARK = '#0a544d';
const CREAM = '#F7F1E4';
const ORANGE = '#e67e22';
const NAVY = '#1a2340';
const WHATSAPP = 'http://wa.me/+918291188188';

const STATS = [
  { label: 'SESSIONS MONTHLY', value: '5,000+' },
  { label: 'CONFIDENCE BOOST', value: '92%' },
  { label: 'PARENT CONNECTION', value: '98%' },
  { label: "DAY'S INSIGHTS", value: '15,000+' },
];

const PAIN_POINTS = [
  { label: 'Low confidence', icon: AlertTriangle },
  { label: 'Emotional withdrawal', icon: Heart },
  { label: 'Lack of expression', icon: Ear },
  { label: 'Screen dependency', icon: Sparkles },
];

const MISSION_POINTS = [
  { icon: Heart, text: 'Build trust and emotional connection with every child' },
  { icon: Ear, text: 'Listen carefully to understand feelings and behaviour' },
  { icon: Lightbulb, text: 'Help children grow with confidence and clarity' },
  { icon: Users, text: 'Support parents in understanding their child better' },
];

const VISION_POINTS = [
  { icon: Headphones, text: 'Every child receives personal attention and guidance' },
  { icon: Sparkles, text: 'Emotional growth is valued alongside academics' },
  { icon: MessageCircle, text: "Parents gain deeper insights into their child\u2019s needs" },
  { icon: TrendingUp, text: 'Children develop confidence, focus, and better decision-making' },
];

// Soft tint palette for the Mission / Vision cards (kept distinct from the
// site's core teal/orange so the two cards read as two different moods)
const MISSION_BG = '#EAF5EE';
const MISSION_ACCENT = '#1F8A5F';
const MISSION_CHIP = '#D9EEE1';
const VISION_BG = '#EAF1FB';
const VISION_ACCENT = '#2E5AAC';
const VISION_CHIP = '#DCE6FA';

// Inline inquiry form (mirrors the "Begin The Journey" block from dacharya.com)
const InquiryForm = () => {
  const { toast } = useToast();
  const [form, setForm] = React.useState({ parent_type: '', parent_name: '', whatsapp: '', child_age: '' });
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim() || !form.whatsapp.trim()) {
      toast({ title: 'Please share your name and WhatsApp number.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        parent_name: form.parent_name.trim(),
        phone: form.whatsapp.trim(),
        child_age: form.child_age ? parseInt(form.child_age, 10) : 0,
        message: `Source: About page. Parent role: ${form.parent_type || 'Not specified'}.`,
      });
      setSubmitted(true);
      toast({ title: "Thanks! We'll reach out on WhatsApp shortly." });
      setForm({ parent_type: '', parent_name: '', whatsapp: '', child_age: '' });
    } catch (err) {
      toast({ title: 'Could not send inquiry', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-6" data-testid="about-inquiry-submitted">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <CheckCircle className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold" style={{ fontFamily: 'Georgia, serif', color: NAVY }}>Inquiry Received</h3>
        <p className="mt-2 text-slate-600 text-sm">We&rsquo;ll reach out on WhatsApp within 24 hours.</p>
        <Button variant="outline" onClick={() => setSubmitted(false)} className="mt-4">Send another</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="about-inquiry-form">
      <div>
        <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Parent</label>
        <select
          value={form.parent_type}
          onChange={(e) => setForm({ ...form, parent_type: e.target.value })}
          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white text-slate-500 focus:outline-none focus:border-slate-400"
          data-testid="about-parent-type"
        >
          <option value="">Select</option>
          <option>Mother</option>
          <option>Father</option>
          <option>Guardian</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Parent Name</label>
        <Input placeholder="Your name" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="mt-1.5 rounded-lg" data-testid="about-parent-name" />
      </div>
      <div>
        <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">WhatsApp Number *</label>
        <Input placeholder="+91 XXXX XXXX XX" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1.5 rounded-lg" data-testid="about-whatsapp" />
      </div>
      <div>
        <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Child&rsquo;s Age</label>
        <select
          value={form.child_age}
          onChange={(e) => setForm({ ...form, child_age: e.target.value })}
          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white text-slate-500 focus:outline-none focus:border-slate-400"
          data-testid="about-child-age"
        >
          <option value="">Select</option>
          {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <Button
        type="submit"
        disabled={submitting}
        className="w-full text-white rounded-full tracking-wider text-sm font-semibold h-12"
        style={{ background: 'linear-gradient(90deg,#f39c12,#e67e22)' }}
        data-testid="about-submit-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        SUBMIT INQUIRY
      </Button>
    </form>
  );
};

// Mission / Vision card — corner icon badge + bold headline + body + icon-badged bullet list
const MissionVisionCard = ({ card }) => {
  const Icon = card.icon;
  const iconChip = (
    <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-white" style={{ boxShadow: `0 0 0 6px ${card.chip}` }}>
      <Icon className="w-6 h-6" style={{ color: card.accent }} />
    </div>
  );
  const textBlock = (
    <div>
      <div className="text-sm font-semibold" style={{ color: ORANGE, fontFamily: 'Montserrat, sans-serif' }}>Our</div>
      <h3 className="text-3xl font-extrabold" style={{ fontFamily: 'Montserrat, sans-serif', color: card.accent }}>{card.title}</h3>
    </div>
  );
  return (
    <div
      className={`rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow border border-slate-100 ${card.order}`}
      style={{ background: card.bg }}
      data-testid={`about-${card.title.toLowerCase()}-card`}
    >
      <div className="flex items-start justify-between gap-3 mb-6">
        {card.iconPosition === 'left' ? (<>{iconChip}{textBlock}</>) : (<>{textBlock}{iconChip}</>)}
      </div>
      <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>{card.headline}</p>
      <p className="mt-3 text-slate-600 leading-relaxed">{card.body}</p>
      <ul className="mt-5 space-y-3">
        {card.points.map((p) => {
          const PointIcon = p.icon;
          return (
            <li key={p.text} className="flex items-center gap-3 text-sm text-slate-700">
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: card.chip }}
              >
                <PointIcon className="w-4 h-4" style={{ color: card.accent }} />
              </span>
              <span>{p.text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const About = () => (
  <div className="min-h-screen" style={{ background: CREAM, fontFamily: 'Inter, system-ui, sans-serif' }}>
    <Navigation />

    {/* ================= HERO ================= */}
    <section
      className="relative overflow-hidden"
      style={{ background: '#0F1E3D' }}
      data-testid="about-hero-section"
    >
      <div className="relative max-w-7xl mx-auto px-4 pt-16 sm:pt-24 pb-16 sm:pb-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* Left — text */}
        <div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-white"
            style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
            data-testid="about-hero-title"
          >
            Every Child Has Something to Say.
            <br />
            <span style={{ color: '#f5a623' }}>We Make Sure Someone Truly Listens.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl" style={{ color: '#c8d3e6' }}>
            In a world where children are constantly connected digitally but feel emotionally unheard, SANJAYA creates a safe space for expression without judgment, pressure, or teaching.
          </p>
          <a
            href={WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-hero-cta"
            className="mt-8 inline-flex items-center gap-2.5 text-white rounded-full pl-4 pr-6 py-3 tracking-wide text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-shadow"
            style={{ background: 'linear-gradient(90deg,#f5a623,#e67e22)' }}
          >
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/25"
              aria-hidden="true"
            >
              {/* WhatsApp glyph */}
              <svg viewBox="0 0 32 32" width="16" height="16" fill="#fff">
                <path d="M19.11 17.87c-.27-.14-1.62-.8-1.87-.9-.25-.09-.43-.14-.62.14-.18.27-.71.9-.87 1.08-.16.18-.32.2-.59.07-.27-.14-1.15-.42-2.19-1.35-.81-.72-1.35-1.61-1.51-1.88-.16-.27-.02-.42.12-.55.12-.12.27-.32.4-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.62-1.5-.85-2.05-.22-.53-.45-.46-.62-.47l-.53-.01c-.18 0-.48.07-.73.34-.25.27-.96.94-.96 2.29 0 1.35.99 2.66 1.13 2.85.14.18 1.95 2.98 4.72 4.18.66.28 1.18.45 1.58.58.66.21 1.26.18 1.74.11.53-.08 1.62-.66 1.85-1.3.23-.65.23-1.2.16-1.32-.07-.11-.25-.18-.52-.32zM16.04 5.33c-5.9 0-10.7 4.79-10.7 10.68 0 1.88.49 3.71 1.42 5.33L5.28 27l5.86-1.53c1.57.86 3.34 1.31 5.14 1.31h.01c5.9 0 10.7-4.79 10.7-10.68 0-2.85-1.11-5.53-3.12-7.55A10.66 10.66 0 0 0 16.04 5.33z" />
              </svg>
            </span>
            Start Your Child&rsquo;s Listening Journey
          </a>
        </div>

        {/* Right — circular photo with hand-drawn ring/dot accents, sitting directly
            on the navy background (no baked-in card behind it) */}
        <div className="relative flex items-center justify-center py-6" data-testid="about-hero-image">
          <div className="relative w-full max-w-[440px] aspect-square">
            {/* decorative ring, arcs & dots — pure SVG so it blends into the navy bg */}
            <svg viewBox="0 0 440 440" className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }} aria-hidden="true">
              <circle cx="220" cy="220" r="204" fill="none" stroke="#f5a623" strokeWidth="6" strokeLinecap="round" strokeDasharray="210 14 90 14" transform="rotate(-100 220 220)" opacity="0.95" />
              <circle cx="220" cy="220" r="204" fill="none" stroke="#1a8f80" strokeWidth="5" strokeLinecap="round" strokeDasharray="70 460" strokeDashoffset="-18" transform="rotate(-100 220 220)" opacity="0.9" />
              <path d="M 30 150 A 204 204 0 0 1 70 55" fill="none" stroke="#1a8f80" strokeWidth="3" strokeLinecap="round" opacity="0.85" />
              <path d="M 60 385 A 204 204 0 0 0 150 424" fill="none" stroke="#f7f7f2" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <path d="M 40 400 A 204 204 0 0 0 110 428" fill="none" stroke="#f7f7f2" strokeWidth="2.5" strokeLinecap="round" opacity="0.4" />
            </svg>
            <span className="absolute w-4 h-4 rounded-full" style={{ background: '#1a8f80', top: '10%', left: '2%' }} />
            <span className="absolute w-5 h-5 rounded-full" style={{ background: '#f5a623', bottom: '6%', right: '4%' }} />

            <img
              src="/images/about-hero-child-circle.png"
              alt="Child in a Sanjaya listening session with an observer on video call"
              className="absolute rounded-full shadow-2xl object-cover w-[82%] h-[82%] top-[9%] left-[9%]"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>

    {/* ================= PROBLEM STATEMENT ================= */}
    <section className="py-20 px-4 bg-white" data-testid="about-problem-section">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>
          Today&rsquo;s Children Are Surrounded, Yet Alone
        </h2>
        <p className="mt-6 text-slate-600 max-w-3xl mx-auto leading-relaxed text-base sm:text-lg">
          Children today spend hours on screens, but very little time being genuinely heard. Parents are busy, conversations are short, and feelings stay unspoken.
        </p>

        <div className="mt-12">
          <p className="text-lg font-semibold" style={{ color: NAVY, fontFamily: 'Georgia, serif' }}>
            Over time, this leads to:
          </p>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {PAIN_POINTS.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.label}
                  className="rounded-2xl p-6 border border-slate-100 shadow-sm bg-white hover:shadow-md transition-shadow"
                  style={{ background: CREAM }}
                  data-testid={`about-pain-${it.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 mx-auto" style={{ background: '#fdecea' }}>
                    <Icon className="w-6 h-6" style={{ color: '#e74c3c' }} />
                  </div>
                  <p className="font-semibold text-slate-900 text-sm">{it.label}</p>
                </div>
              );
            })}
          </div>

          <div
            className="mt-10 inline-block rounded-2xl px-6 py-4 border-2 border-dashed"
            style={{ borderColor: ORANGE, background: '#fff9f0' }}
            data-testid="about-callout"
          >
            <p className="text-lg font-bold" style={{ color: ORANGE, fontFamily: 'Georgia, serif' }}>
              The biggest problem? No one is just listening.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ================= FOUNDER ================= */}
    <section className="py-20 px-4" style={{ background: CREAM }} data-testid="about-founder-section">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-2 order-2 lg:order-1">
          <div className="rounded-[2rem] overflow-hidden bg-gradient-to-br from-amber-100 to-rose-100 aspect-[4/5] max-w-xs mx-auto lg:mx-0 shadow-md">
            <img
              src="https://customer-assets.emergentagent.com/job_912f38ca-962a-42a7-9f88-0f9aebc0953f/artifacts/i0ck2cs3_image.png"
              alt="Smt. Punam Jaiswal"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        <div className="lg:col-span-3 order-1 lg:order-2">
          <p className="text-lg font-semibold mb-2" style={{ color: TEAL }}>The Visionary Behind Sanjaya</p>
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>
            Founder &amp; Former Principal :- Smt. Punam Jaiswal
          </h2>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2" style={{ background: '#e7f4f2', color: TEAL }}>
            <Award className="w-4 h-4" />
            <span className="text-sm font-semibold">35+ Years of Experience in Education and Child Psychology</span>
          </div>
          <blockquote className="mt-6 relative pl-6 border-l-4 py-2" style={{ borderColor: ORANGE }}>
            <p className="text-xl text-slate-800 leading-relaxed italic" style={{ fontFamily: 'Georgia, serif' }}>
              &ldquo;Every child has a story to tell. My role is simply to listen, understand, and help parents see the beautiful complexity of their child&rsquo;s world.&rdquo;
            </p>
          </blockquote>
          <p className="mt-6 text-slate-600 leading-relaxed">
            Smt. Punam Jaiswal has dedicated her life to understanding the intersection of academic pressure and child emotional growth. Her unique observation-first approach has transformed thousands of parent-child relationships.
          </p>
        </div>
      </div>
    </section>

    {/* ================= MISSION & VISION ================= */}
    <section className="py-20 px-4 bg-white overflow-hidden" data-testid="about-mission-vision-section">
      <div className="max-w-6xl mx-auto text-center mb-14">
        <p className="text-xs tracking-[0.3em] font-bold uppercase mb-3" style={{ color: TEAL }}>Our Purpose</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold" style={{ fontFamily: 'Montserrat, sans-serif', color: NAVY }}>
          Our Mission &amp; <span style={{ color: TEAL }}>Our Vision</span>
        </h2>
        <p className="mt-3 text-slate-600">Listening today. Transforming tomorrow.</p>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.05fr_1fr] gap-10 lg:gap-6 items-stretch">
        {[
          { key: 'mission', title: 'Mission', icon: Target, iconPosition: 'left', headline: 'To understand every child before guiding them.', body: 'We provide a safe and nurturing environment where children can openly express their thoughts, feelings, and concerns with confidence.', points: MISSION_POINTS, bg: MISSION_BG, accent: MISSION_ACCENT, chip: MISSION_CHIP, order: 'lg:order-1' },
        ].map((card) => (
          <MissionVisionCard key={card.key} card={card} />
        ))}

        {/* Center visual — blob-framed photo blending into an illustrated ground, badge + quote floating on top */}
        <div className="relative flex items-center justify-center lg:order-2" data-testid="about-mission-vision-visual">
          <div className="relative w-full max-w-[340px] mx-auto" style={{ aspectRatio: '4 / 5' }}>
            <svg width="0" height="0" className="absolute" aria-hidden="true">
              <defs>
                <clipPath id="about-photo-blob" clipPathUnits="objectBoundingBox">
                  <path d="M0.5,0 C0.78,0 1,0.22 1,0.42 L1,0.75 C1,0.9 0.85,1 0.65,1 L0.35,1 C0.15,1 0,0.9 0,0.75 L0,0.42 C0,0.22 0.22,0 0.5,0 Z" />
                </clipPath>
              </defs>
            </svg>
            <img
              src="https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&q=80&auto=format&fit=crop"
              alt="A smiling child feeling heard and understood"
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                clipPath: 'url(#about-photo-blob)',
                filter: 'drop-shadow(0 22px 34px rgba(26,35,64,0.35))',
                WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 74%, transparent 96%)',
                maskImage: 'linear-gradient(to bottom, black 0%, black 74%, transparent 96%)',
              }}
              loading="lazy"
            />

            {/* Illustrated ground — faint skyline, tree, kite-flying kids, birds — shows through as the photo fades out */}
            <svg
              viewBox="0 0 340 190"
              className="absolute bottom-0 left-0 w-full pointer-events-none"
              style={{ height: '46%' }}
              preserveAspectRatio="xMidYMax slice"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="about-ground-fade" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={TEAL} stopOpacity="0" />
                  <stop offset="100%" stopColor={TEAL} stopOpacity="0.14" />
                </linearGradient>
              </defs>
              <g opacity="0.14" fill={NAVY}>
                <rect x="200" y="90" width="18" height="70" />
                <rect x="222" y="70" width="14" height="90" />
                <rect x="240" y="100" width="16" height="60" />
                <rect x="260" y="80" width="12" height="80" />
                <rect x="276" y="105" width="20" height="55" />
                <rect x="300" y="88" width="14" height="72" />
              </g>
              <ellipse cx="170" cy="188" rx="200" ry="40" fill="url(#about-ground-fade)" />
              <g>
                <rect x="27" y="120" width="6" height="45" rx="2" fill="#8a6b3d" />
                <circle cx="30" cy="122" r="15" fill="#3f9142" />
                <circle cx="18" cy="132" r="12" fill="#4caa4f" />
                <circle cx="42" cy="132" r="12" fill="#4caa4f" />
              </g>
              <g stroke={TEAL} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.85">
                <path d="M148 98 q4.5-4.5 9 0 q4.5-4.5 9 0" />
                <path d="M185 86 q4-4 8 0 q4-4 8 0" />
                <path d="M215 104 q3.5-3.5 7 0 q3.5-3.5 7 0" />
                <path d="M112 108 q3.5-3.5 7 0 q3.5-3.5 7 0" />
                <path d="M250 92 q3.5-3.5 7 0 q3.5-3.5 7 0" />
              </g>
              {[
                { x: 100, s: 0.65, color: ORANGE, dir: 1 },
                { x: 150, s: 0.8, color: TEAL, dir: 1 },
                { x: 205, s: 0.95, color: ORANGE, dir: -1 },
                { x: 265, s: 1.1, color: TEAL, dir: -1 },
              ].map((k) => (
                <g key={k.x} transform={`translate(${k.x} 165) scale(${k.s})`}>
                  <path d={`M4 -30 L${k.dir * 18} -70`} stroke={NAVY} strokeWidth="1" strokeDasharray="2 2" opacity="0.5" fill="none" />
                  <path d={`M${k.dir * 18} -78 l8 8 l-8 8 l-8 -8 z`} fill={k.color} opacity="0.85" />
                  <path d={`M${k.dir * 18} -62 l-2 8 l4 0 z`} fill={k.color} opacity="0.5" />
                  <ellipse cx="0" cy="-14" rx="7" ry="13" fill={NAVY} opacity="0.8" />
                  <circle cx="0" cy="-30" r="6" fill={NAVY} opacity="0.8" />
                  <path d="M-4 0 l-3 10 M4 0 l3 10" stroke={NAVY} strokeWidth="2.5" strokeLinecap="round" opacity="0.8" />
                  <path d={`M4 -20 L${k.dir * 10} -28`} stroke={NAVY} strokeWidth="2" strokeLinecap="round" opacity="0.8" />
                </g>
              ))}
            </svg>

            {/* Logo badge overlapping the top of the photo */}
            <div
              className="absolute left-1/2 -translate-x-1/2 -top-4 bg-white rounded-full shadow-lg pl-2 pr-4 py-2 flex items-center gap-2 border border-slate-100"
              data-testid="about-mission-vision-logo-badge"
            >
              <img src="/images/sanjaya-logo.png" alt="Sanjaya" className="h-8 w-8 object-contain rounded-full" loading="lazy" />
              <div className="leading-tight text-left">
                <div className="text-xs font-extrabold tracking-wide" style={{ color: NAVY, fontFamily: 'Montserrat, sans-serif' }}>SANJAYA</div>
                <div className="text-[8px] font-semibold tracking-[0.15em] uppercase text-slate-400">The Observer</div>
              </div>
            </div>

            {/* Quote card, floating where the photo begins to fade into the illustrated ground */}
            <div
              className="absolute left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg px-4 py-3 w-[78%] text-center border border-white"
              style={{ top: '50%' }}
              data-testid="about-mission-vision-quote"
            >
              <p className="text-sm leading-snug" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: TEAL }}>
                “A child who is heard grows into a confident tomorrow.”
              </p>
              <Heart className="w-4 h-4 mx-auto mt-1.5" style={{ color: TEAL }} strokeWidth={1.75} />
            </div>
          </div>
        </div>

        {[
          { key: 'vision', title: 'Vision', icon: Compass, iconPosition: 'right', headline: 'To create emotionally confident and future ready children.', body: 'We believe every child deserves to be heard, understood, and guided with care for better growth and development.', points: VISION_POINTS, bg: VISION_BG, accent: VISION_ACCENT, chip: VISION_CHIP, order: 'lg:order-3' },
        ].map((card) => (
          <MissionVisionCard key={card.key} card={card} />
        ))}
      </div>
    </section>

    {/* ================= STATS ================= */}
    <section className="py-16 px-4" style={{ background: TEAL }} data-testid="about-stats-section">
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
        {STATS.map((s) => (
          <div key={s.label}>
            <div className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>{s.value}</div>
            <div className="mt-2 text-[11px] tracking-[0.2em] font-semibold uppercase opacity-90">{s.label}</div>
          </div>
        ))}
      </div>
    </section>

    {/* ================= INQUIRY / BEGIN THE JOURNEY ================= */}
    <section className="py-20 px-4 bg-white" data-testid="about-inquiry-section">
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>Begin The Journey</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Fill out the inquiry form or reach out directly via WhatsApp for immediate registration.
          </p>
          <div className="mt-6 rounded-2xl p-5" style={{ background: '#e7f4f2' }}>
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Prefer direct chat?</div>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-2 text-2xl font-bold"
              style={{ color: TEAL, fontFamily: 'Georgia, serif' }}
              data-testid="about-whatsapp-link"
            >
              <Phone className="w-5 h-5" /> +91 8291188188
            </a>
          </div>
        </div>
        <div className="rounded-2xl p-6 sm:p-8" style={{ background: CREAM }}>
          <InquiryForm />
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default About;
